/**
 * PlanBoard.jsx — Feb 2026 · Phase 84.29
 *
 * Notion-style content calendar for the Marketing project.
 *
 *  • Month grid (Sun–Sat) with prev / next / Today; multiple entries per day.
 *  • Each entry card shows: Title (prominent), status dot, content-type tag,
 *    platform tag(s), and the scheduled time chip.
 *  • Filters: Status · Content type · Platform · Pillar · Owner.
 *  • Timezone toggle (IST · EST · PST · MST) via IANA IDs.
 *  • Detail panel:
 *      – Title / Topic
 *      – Status (Planned · Scheduled · Published · Suspended)
 *      – Content types (multi-select, add-your-own)
 *      – Platforms (multi-select) · Pillars (multi-select, add-your-own)
 *      – Date + time (with best-time suggestions per platform+type)
 *      – Owner
 *      – Production checklist (14 defaults, add/remove per item, completed count)
 *      – Asset links (Drive URLs)
 *      – Final Copy — per-platform sections with hashtags
 *      – Dependencies (task / person / status)
 *      – Comments
 *      – Approval + Mark-published lifecycle
 *  • Drag-and-drop reschedule across days (native HTML5 DataTransfer).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Instagram, Linkedin, Youtube, MapPin, Music2, AtSign, PencilRuler, Palette, Pin,
  Sparkles, Send, Trash2, Image as ImageIcon, Loader2, Pencil,
  ChevronLeft, ChevronRight, Check, X, CheckCircle2, Globe2, CalendarPlus, Clock,
  Plus, Link as LinkIcon, MessageSquare, ClipboardCheck, ListTree, Filter,
  Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, Repeat2, ThumbsUp,
} from "lucide-react";
import { toast } from "sonner";
import { STARTER_HOOKS } from "./marketingData";

/* ------------------------------------------------------------------------- */
/*  Timezones                                                                 */
/* ------------------------------------------------------------------------- */
const TIMEZONES = [
  { id: "Asia/Kolkata",       label: "IST", city: "Mumbai · New Delhi" },
  { id: "America/New_York",   label: "EST", city: "New York · Toronto" },
  { id: "America/Los_Angeles",label: "PST", city: "SF · LA · Vancouver" },
  { id: "America/Denver",     label: "MST", city: "Denver · Phoenix" },
];
function fmtInTz(iso, tz, opts) { if (!iso) return ""; return new Intl.DateTimeFormat("en-US", { timeZone: tz, ...opts }).format(new Date(iso)); }
function localToUtcISO(dateStr, timeStr, tz) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mi] = timeStr.split(":").map(Number);
  let utc = Date.UTC(y, m - 1, d, hh, mi);
  for (let i = 0; i < 2; i++) {
    const g = new Date(utc);
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(g).reduce((a, p) => { a[p.type] = p.value; return a; }, {});
    const asLocal = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour === 24 ? 0 : +parts.hour, +parts.minute);
    const drift = utc - asLocal;
    utc += drift;
    if (Math.abs(drift) < 60000) break;
  }
  return new Date(utc).toISOString();
}
function tzDateKey(iso, tz) {
  if (!iso) return null;
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(iso)).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  return `${p.year}-${p.month}-${p.day}`;
}
function tzTimeStr(iso, tz) { return fmtInTz(iso, tz, { hour: "2-digit", minute: "2-digit", hour12: true }); }

/* ------------------------------------------------------------------------- */
/*  Domain constants                                                          */
/* ------------------------------------------------------------------------- */
const CHANNELS = [
  { id: "instagram",  label: "Instagram",        color: "#E1306C", bg: "#FCE4EC", fg: "#AD1457", Icon: Instagram },
  { id: "linkedin",   label: "LinkedIn",         color: "#0A66C2", bg: "#E3F2FD", fg: "#0D47A1", Icon: Linkedin },
  { id: "youtube",    label: "YouTube",          color: "#FF0000", bg: "#FFEBEE", fg: "#B71C1C", Icon: Youtube },
  { id: "tiktok",     label: "TikTok",           color: "#000000", bg: "#F5F5F5", fg: "#111827", Icon: Music2 },
  { id: "x",          label: "X",                color: "#111827", bg: "#E5E7EB", fg: "#111827", Icon: AtSign },
  { id: "threads",    label: "Threads",          color: "#374151", bg: "#FCE7F3", fg: "#831843", Icon: AtSign },
  { id: "pinterest",  label: "Pinterest",        color: "#E60023", bg: "#FEE2E2", fg: "#991B1B", Icon: Pin },
  { id: "behance",    label: "Behance",          color: "#1769FF", bg: "#DBEAFE", fg: "#1E40AF", Icon: Palette },
  { id: "gmb",        label: "Google Business",  color: "#34A853", bg: "#DCFCE7", fg: "#166534", Icon: MapPin },
];
const CHANNEL_MAP = Object.fromEntries(CHANNELS.map((c) => [c.id, c]));

const STATUSES = [
  { id: "planned",   label: "Planned",   dot: "#94a3b8", pill: "bg-slate-100 text-slate-700" },
  { id: "scheduled", label: "Scheduled", dot: "#F59E0B", pill: "bg-amber-100 text-amber-800" },
  { id: "published", label: "Published", dot: "#10B981", pill: "bg-emerald-100 text-emerald-800" },
  { id: "suspended", label: "Suspended", dot: "#EF4444", pill: "bg-red-100 text-red-700" },
];
// Phase 153 — Home Task Hub's "Social Posts" tab introduced a richer 5-stage
// lifecycle (Idea · In Progress · Review · Scheduled · Published). Those
// three new mid-stage values are display-only here (Plan Board's own
// filters/buttons are unchanged) so drafts created from that tab still
// render correctly if opened here for deeper editing.
const STATUS_MAP = Object.fromEntries([
  ...STATUSES.map((s) => [s.id, s]),
  ["idea", { id: "idea", label: "Idea", dot: "#94a3b8", pill: "bg-slate-100 text-slate-700" }],
  ["in_progress", { id: "in_progress", label: "In Progress", dot: "#3B82F6", pill: "bg-blue-100 text-blue-800" }],
  ["review", { id: "review", label: "Review", dot: "#A855F7", pill: "bg-purple-100 text-purple-800" }],
]);

const DEFAULT_CONTENT_TYPES = ["Static Post", "Reel/Short", "Story", "Carousel", "Long Form Video", "YouTube Shorts", "Blog", "Newsletter", "Pinterest Pin", "Community"];
const DEFAULT_CHECKLIST_KEYS = ["Thumbnail", "Visual Assets", "Aspect ratio 1:1", "Aspect ratio 4:3", "Description/Captions", "First comment", "Title Hook", "Copy Hook", "CTA", "Title", "Keywords", "Emojis", "Lead Magnet"];

/* ------------------------------------------------------------------------- */
/*  Root                                                                      */
/* ------------------------------------------------------------------------- */
export default function PlanBoard({ drafts, onChanged, members = [] }) {
  const [tz, setTz] = useState(() => localStorage.getItem("planboard.tz") || "Asia/Kolkata");
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selectedId, setSelectedId] = useState(null);
  const [creatingForDate, setCreatingForDate] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [filters, setFilters] = useState({ status: "", contentType: "", platform: "", owner: "" });
  const [customTypes, setCustomTypes] = useState(() => JSON.parse(localStorage.getItem("planboard.customTypes") || "[]"));
  const [bestTimes, setBestTimes] = useState({});

  useEffect(() => { localStorage.setItem("planboard.tz", tz); }, [tz]);
  useEffect(() => { localStorage.setItem("planboard.customTypes", JSON.stringify(customTypes)); }, [customTypes]);
  useEffect(() => { api.get("/marketing/best-times").then((r) => setBestTimes(r.data?.suggestions || {})).catch(() => {}); }, []);

  const allContentTypes = useMemo(() => Array.from(new Set([...DEFAULT_CONTENT_TYPES, ...customTypes])), [customTypes]);

  const selected = useMemo(() => drafts.find((d) => d.id === selectedId) || null, [drafts, selectedId]);

  const filtered = useMemo(() => drafts.filter((d) => {
    if (filters.status && (d.status || "planned") !== filters.status) return false;
    if (filters.contentType && !(d.content_types || []).includes(filters.contentType) && d.content_type !== filters.contentType) return false;
    if (filters.platform && !(d.channels || []).includes(filters.platform)) return false;
    if (filters.owner && d.owner_id !== filters.owner && d.created_by !== filters.owner) return false;
    return true;
  }), [drafts, filters]);

  const byDay = useMemo(() => {
    const m = {};
    for (const d of filtered) {
      if (!d.scheduled_for) continue;
      const key = tzDateKey(d.scheduled_for, tz);
      if (!key) continue;
      (m[key] = m[key] || []).push(d);
    }
    for (const k of Object.keys(m)) m[k].sort((a, b) => new Date(a.scheduled_for) - new Date(b.scheduled_for));
    return m;
  }, [filtered, tz]);

  const unscheduled = useMemo(() => filtered.filter((d) => !d.scheduled_for), [filtered]);

  const openForEntry = useCallback((id) => {
    setSelectedId(id);
    setCreatingForDate(null);
    setModalOpen(true);
  }, []);
  const openForNew = useCallback((dateKey) => {
    setCreatingForDate(dateKey);
    setSelectedId(null);
    setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setModalOpen(false);
    setTimeout(() => { setSelectedId(null); setCreatingForDate(null); }, 200);
  }, []);

  const reschedule = useCallback(async (id, newDateKey) => {
    const d = drafts.find((x) => x.id === id);
    if (!d) return;
    let hh = 9, mi = 0;
    if (d.scheduled_for) {
      const t = fmtInTz(d.scheduled_for, tz, { hour: "2-digit", minute: "2-digit", hour12: false });
      [hh, mi] = t.split(":").map(Number);
    }
    const iso = localToUtcISO(newDateKey, `${String(hh).padStart(2, "0")}:${String(mi).padStart(2, "0")}`, tz);
    try { await api.patch(`/marketing/drafts/${id}`, { scheduled_for: iso, scheduled_tz: tz }); toast.success("Rescheduled"); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [drafts, tz, onChanged]);

  const unschedule = useCallback(async (id) => {
    try { await api.patch(`/marketing/drafts/${id}`, { scheduled_for: null, status: "planned" }); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [onChanged]);

  return (
    <div className="space-y-3" data-testid="plan-board">
      <FilterBar
        filters={filters} setFilters={setFilters}
        contentTypes={allContentTypes}
        members={members}
      />

      {/* -------------------- Full-screen Calendar -------------------- */}
      <div className="card-flat p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <button onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))} className="p-1.5 hover:bg-slate-100 rounded-sm" data-testid="planboard-prev-month"><ChevronLeft size={14} /></button>
            <div className="font-display text-lg font-bold text-slate-900 min-w-[160px] text-center" data-testid="planboard-month-label">
              {cursor.toLocaleString([], { month: "long", year: "numeric" })}
            </div>
            <button onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))} className="p-1.5 hover:bg-slate-100 rounded-sm" data-testid="planboard-next-month"><ChevronRight size={14} /></button>
            <button onClick={() => { const d = new Date(); d.setDate(1); setCursor(d); }} className="ml-2 text-[11px] px-2 py-1 rounded-sm border border-slate-200 hover:border-slate-400" data-testid="planboard-today">Today</button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => openForNew(tzDateKey(new Date().toISOString(), tz))}
              className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white h-8 text-xs"
              data-testid="planboard-new-post"
            >
              <Plus size={12} className="mr-1" /> New post
            </Button>
            <Globe2 size={13} className="text-slate-400" />
            <Select value={tz} onValueChange={setTz}>
              <SelectTrigger className="h-8 rounded-sm text-xs w-[190px]" data-testid="planboard-tz"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    <span className="font-semibold mr-1.5">{t.label}</span>
                    <span className="text-slate-500 text-[11px]">· {t.city}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <CalendarGrid
          cursor={cursor} byDay={byDay} tz={tz} selectedId={selectedId}
          onSelect={openForEntry}
          onCreate={openForNew}
          onDayClick={openForNew}
          onDrop={reschedule}
        />

        {unscheduled.length > 0 && (
          <div
            className="mt-4 border-2 border-dashed border-slate-200 rounded-sm p-3 bg-slate-50"
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
            onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain"); if (id) unschedule(id); }}
            data-testid="planboard-unscheduled"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="label-overline">Unscheduled ({unscheduled.length})</div>
              <div className="text-[10px] text-slate-500">Drag onto a date to schedule · Click to edit</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {unscheduled.map((d) => (
                <DraftChip key={d.id} draft={d} tz={tz} isSelected={false} onSelect={() => openForEntry(d.id)} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* -------------------- Editor Modal -------------------- */}
      <Dialog open={modalOpen} onOpenChange={(v) => { if (!v) closeModal(); }}>
        <DialogContent
          className="max-w-3xl w-[95vw] max-h-[90vh] overflow-hidden p-0 rounded-md bg-white"
          data-testid="planboard-editor-modal"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{selected ? "Edit post" : "New post"}</DialogTitle>
          </DialogHeader>
          <PostEditor
            key={selected?.id || creatingForDate || "new"}
            draft={selected}
            creatingForDate={creatingForDate}
            tz={tz}
            allContentTypes={allContentTypes}
            customTypes={customTypes} setCustomTypes={setCustomTypes}
            members={members}
            bestTimes={bestTimes}
            onChanged={onChanged}
            onCreated={(newId) => { setSelectedId(newId); setCreatingForDate(null); }}
            onDeleted={() => { closeModal(); onChanged && onChanged(); }}
            onClose={closeModal}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/*  Filter bar                                                                */
/* ------------------------------------------------------------------------- */
const ANY = "__any__";
function FilterBar({ filters, setFilters, contentTypes, members }) {
  const set = (k, v) => setFilters({ ...filters, [k]: v === ANY ? "" : v });
  const active = Object.values(filters).some(Boolean);
  return (
    <div className="card-flat px-3 py-2 flex flex-wrap items-center gap-2" data-testid="planboard-filters">
      <Filter size={13} className="text-slate-400" />
      <FilterSelect testId="filter-status" placeholder="Status" value={filters.status || ANY} onChange={(v) => set("status", v)} options={[{ v: ANY, l: "Any status" }, ...STATUSES.map((s) => ({ v: s.id, l: s.label }))]} />
      <FilterSelect testId="filter-type" placeholder="Content type" value={filters.contentType || ANY} onChange={(v) => set("contentType", v)} options={[{ v: ANY, l: "Any type" }, ...contentTypes.map((t) => ({ v: t, l: t }))]} />
      <FilterSelect testId="filter-platform" placeholder="Platform" value={filters.platform || ANY} onChange={(v) => set("platform", v)} options={[{ v: ANY, l: "Any platform" }, ...CHANNELS.map((c) => ({ v: c.id, l: c.label }))]} />
      <FilterSelect testId="filter-owner" placeholder="Owner" value={filters.owner || ANY} onChange={(v) => set("owner", v)} options={[{ v: ANY, l: "Anyone" }, ...(members || []).map((m) => ({ v: m.id, l: m.name }))]} />
      {active && (
        <button onClick={() => setFilters({ status: "", contentType: "", platform: "", owner: "" })} className="text-[10px] text-slate-500 hover:text-red-600 ml-auto" data-testid="planboard-filters-clear">
          <X size={10} className="inline" /> Clear
        </button>
      )}
    </div>
  );
}
function FilterSelect({ value, onChange, options, placeholder, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-7 rounded-sm text-[11px] min-w-[110px] w-auto" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

/* ------------------------------------------------------------------------- */
/*  Calendar grid                                                             */
/* ------------------------------------------------------------------------- */
function CalendarGrid({ cursor, byDay, tz, selectedId, onSelect, onCreate, onDayClick, onDrop }) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const startOffset = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayKey = tzDateKey(new Date().toISOString(), tz);
  const cellKey = (day) => `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  return (
    <>
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-slate-400 mb-1">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="px-2 py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: startOffset }).map((_, i) => <div key={`o-${i}`} className="min-h-[150px] bg-slate-50 rounded-sm" />)}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
          const key = cellKey(day);
          const items = byDay[key] || [];
          const isToday = key === todayKey;
          return (
            <div key={day}
              className={`group min-h-[150px] p-1.5 rounded-sm border transition-colors cursor-pointer ${isToday ? "border-[#F54800] bg-[#F54800]/5" : "border-slate-100 bg-white hover:border-slate-300 hover:bg-slate-50/40"}`}
              onClick={(e) => {
                // Only trigger day-click when clicking the cell background (not on entry cards)
                if (e.target === e.currentTarget || e.target.dataset?.cellBg === "1") onDayClick(key);
              }}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
              onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain"); if (id) onDrop(id, key); }}
              data-testid={`planboard-cell-${key}`}>
              <div className="flex items-center justify-between" data-cell-bg="1">
                <div className={`text-[11px] font-bold ${isToday ? "text-[#F54800]" : "text-slate-500"}`} data-cell-bg="1">{day}</div>
                <button
                  onClick={(e) => { e.stopPropagation(); onCreate(key); }}
                  className="opacity-0 group-hover:opacity-100 text-[10px] text-slate-400 hover:text-[#F54800]"
                  title="Create a post"
                  data-testid={`planboard-add-${key}`}
                >
                  <CalendarPlus size={11} />
                </button>
              </div>
              <div className="mt-1 space-y-1" data-cell-bg="1">
                {items.map((d) => (
                  <EntryCard
                    key={d.id}
                    draft={d}
                    tz={tz}
                    selected={d.id === selectedId}
                    onSelect={(ev) => { ev.stopPropagation(); onSelect(d.id); }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/** Rich entry card — matches design ref: bold title, status pill, content-type + platform color chips. */
function EntryCard({ draft, tz, selected, onSelect }) {
  const st = STATUS_MAP[draft.status || "planned"];
  const type = (draft.content_types || [])[0] || draft.content_type;
  const channels = draft.channels || [];
  return (
    <div
      draggable
      onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.setData("text/plain", draft.id); e.dataTransfer.effectAllowed = "move"; }}
      onClick={onSelect}
      className={`px-2 py-1.5 rounded-sm border text-left cursor-pointer bg-white shadow-sm hover:shadow-md transition-shadow ${selected ? "ring-2 ring-slate-900 ring-offset-1 border-slate-900" : "border-slate-200 hover:border-slate-400"}`}
      title={draft.title || draft.caption}
      data-testid={`planboard-pill-${draft.id}`}
    >
      {/* Bold Title */}
      <div className="text-[11px] font-bold text-slate-900 truncate leading-tight">
        {draft.title || draft.caption?.split("\n")[0] || "(untitled)"}
      </div>

      {/* Status pill */}
      <div className="mt-1 flex items-center gap-1">
        <span className={`inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-sm ${st?.pill}`}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: st?.dot }} />
          {st?.label}
        </span>
        <span className="text-[9px] font-mono text-slate-400 ml-auto">{tzTimeStr(draft.scheduled_for, tz)}</span>
      </div>

      {/* Content type + colored platform tags */}
      <div className="mt-1 flex items-center gap-1 flex-wrap">
        {type && (
          <span
            className="text-[9px] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700 truncate max-w-[90px]"
            title={type}
          >
            {type}
          </span>
        )}
        {channels.slice(0, 3).map((cid) => {
          const c = CHANNEL_MAP[cid];
          if (!c) return null;
          return (
            <span
              key={cid}
              className="text-[9px] px-1.5 py-0.5 rounded-sm font-medium"
              style={{ background: c.bg, color: c.fg }}
              title={c.label}
            >
              {c.label}
            </span>
          );
        })}
        {channels.length > 3 && <span className="text-[9px] text-slate-400">+{channels.length - 3}</span>}
        {draft.approval_status === "approved" && <CheckCircle2 size={9} className="text-emerald-600 ml-auto" />}
      </div>
    </div>
  );
}

function DraftChip({ draft, tz, isSelected, onSelect }) {
  const st = STATUS_MAP[draft.status || "planned"];
  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.setData("text/plain", draft.id); e.dataTransfer.effectAllowed = "move"; }}
      onClick={onSelect}
      className={`text-[11px] px-2 py-1.5 rounded-sm border cursor-grab flex items-center gap-1.5 bg-white ${isSelected ? "ring-2 ring-slate-900" : "border-slate-200 hover:border-slate-400"}`}
      data-testid={`planboard-unscheduled-${draft.id}`}
    >
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: st?.dot }} />
      <span className="truncate max-w-[160px]">{draft.title || draft.caption.split("\n")[0] || "(untitled)"}</span>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/*  Detail panel — modal-friendly content editor with platform preview        */
/* ------------------------------------------------------------------------- */
function PostEditor({ draft, creatingForDate, tz, allContentTypes, customTypes, setCustomTypes, members, bestTimes, onChanged, onCreated, onDeleted, onClose }) {
  const isNew = !draft && !!creatingForDate;

  // Everything about the item.
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState("planned");
  const [contentTypes, setContentTypes] = useState([]);
  const [channels, setChannels] = useState(new Set(["instagram"]));
  const [caption, setCaption] = useState("");
  const [dateStr, setDateStr] = useState("");
  const [timeStr, setTimeStr] = useState("09:00");
  const [ownerId, setOwnerId] = useState("");
  const [mediaUrls, setMediaUrls] = useState([]);
  const [checklist, setChecklist] = useState({});
  const [assetLinks, setAssetLinks] = useState([]);
  const [perPlatformCopy, setPerPlatformCopy] = useState({});
  const [dependencies, setDependencies] = useState([]);
  const [comments, setComments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newCommentBody, setNewCommentBody] = useState("");
  const [newTypeInput, setNewTypeInput] = useState("");
  const [newChecklistKey, setNewChecklistKey] = useState("");
  const [previewTab, setPreviewTab] = useState("instagram");
  const [priorityId, setPriorityId] = useState("");  // Phase 84.33
  const [openPriorities, setOpenPriorities] = useState([]);  // Phase 84.33
  const [importance, setImportance] = useState("medium");  // Phase 101

  // Load open priorities when the editor mounts.
  useEffect(() => {
    api.get("/priorities/all-open").then((r) => setOpenPriorities(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (draft) {
      setTitle(draft.title || "");
      setStatus(draft.status || "planned");
      setContentTypes(draft.content_types || (draft.content_type ? [draft.content_type] : []));
      setChannels(new Set(draft.channels || ["instagram"]));
      setCaption(draft.caption || "");
      setMediaUrls(draft.media_urls?.length ? draft.media_urls : (draft.media_url ? [draft.media_url] : []));
      setChecklist(draft.checklist || {});
      setAssetLinks(draft.asset_links || []);
      setPerPlatformCopy(draft.per_platform_copy || {});
      setDependencies(draft.dependencies || []);
      setComments(draft.comments || []);
      setOwnerId(draft.owner_id || draft.created_by || "");
      setPriorityId(draft.priority_id || "");
      setImportance(draft.importance || "medium");
      if (draft.scheduled_for) {
        setDateStr(tzDateKey(draft.scheduled_for, tz));
        setTimeStr(fmtInTz(draft.scheduled_for, tz, { hour: "2-digit", minute: "2-digit", hour12: false }));
      } else { setDateStr(""); setTimeStr("09:00"); }
      const firstCh = (draft.channels || ["instagram"])[0];
      setPreviewTab(firstCh);
    } else if (isNew) {
      setTitle(""); setStatus("planned"); setContentTypes([]); setChannels(new Set(["instagram"]));
      setCaption(""); setMediaUrls([]);
      setChecklist(Object.fromEntries(DEFAULT_CHECKLIST_KEYS.map((k) => [k, false])));
      setAssetLinks([]); setPerPlatformCopy({}); setDependencies([]); setComments([]);
      setOwnerId(""); setDateStr(creatingForDate); setTimeStr("09:00");
      setPriorityId("");
      setImportance("medium");
      setPreviewTab("instagram");
    }
  }, [draft?.id, isNew, creatingForDate, tz]);

  const toggleChannel = (id) => setChannels((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleContentType = (t) => setContentTypes((arr) => arr.includes(t) ? arr.filter((x) => x !== t) : [...arr, t]);

  const uploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name} too large`); continue; }
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.path);
      }
      const next = [...mediaUrls, ...uploaded].slice(0, 10);
      setMediaUrls(next);
      if (draft) await api.patch(`/marketing/drafts/${draft.id}`, { media_url: next[0], media_urls: next });
      onChanged && onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = useCallback(async (opts = {}) => {
    const { saveAsDraft = false } = opts;
    if (!(title.trim() || caption.trim())) { toast.error("Add a title or caption"); return; }
    if (channels.size === 0) { toast.error("Pick at least one platform"); return; }
    // Phase 101 — once a post is scheduled on the calendar it's a real task:
    // it needs an Assignee (mirrors to the linked daily task's assignee_id).
    if (!saveAsDraft && dateStr && !ownerId) { toast.error("Pick an Owner before scheduling — every calendar item needs an assignee"); return; }
    setSaving(true);
    try {
      const scheduled_for = saveAsDraft ? null : (dateStr ? localToUtcISO(dateStr, timeStr, tz) : null);
      const payload = {
        title: title.trim() || null,
        caption: caption.trim() || title.trim(),
        channels: Array.from(channels),
        media_url: mediaUrls[0] || null,
        media_urls: mediaUrls,
        scheduled_for,
        scheduled_tz: tz,
        content_types: contentTypes,
        checklist,
        asset_links: assetLinks,
        per_platform_copy: perPlatformCopy,
        dependencies,
        owner_id: ownerId || null,
        priority_id: priorityId || null,  // Phase 84.33 — link post to a weekly priority
        importance: importance || "medium",  // Phase 101 — mirrors onto the linked daily task
      };
      if (draft) {
        if (saveAsDraft) { payload.status = "planned"; }
        else if (status && !scheduled_for) payload.status = status;
        else if (scheduled_for) payload.status = status === "published" ? "published" : "scheduled";
        await api.patch(`/marketing/drafts/${draft.id}`, payload);
        toast.success(saveAsDraft ? "Saved as draft" : "Saved");
      } else {
        const { data } = await api.post("/marketing/drafts", { ...payload, source: "planboard" });
        toast.success(scheduled_for ? "Scheduled" : "Saved as draft");
        onCreated && onCreated(data.id);
      }
      onChanged && onChanged();
      onClose && onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }, [title, caption, channels, mediaUrls, dateStr, timeStr, tz, contentTypes, checklist, assetLinks, perPlatformCopy, dependencies, ownerId, priorityId, importance, draft, status, onCreated, onChanged, onClose]);

  const remove = async () => {
    if (!draft || !window.confirm("Delete this content item?")) return;
    try { await api.delete(`/marketing/drafts/${draft.id}`); toast.success("Deleted"); onDeleted && onDeleted(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const changeStatusInline = async (newStatus) => {
    setStatus(newStatus);
    if (!draft) return;
    try {
      await api.patch(`/marketing/drafts/${draft.id}`, { status: newStatus });
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addComment = async () => {
    if (!draft || !newCommentBody.trim()) return;
    try {
      const { data } = await api.post(`/marketing/drafts/${draft.id}/comments`, { body: newCommentBody.trim() });
      setComments(data.comments || []);
      setNewCommentBody("");
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteComment = async (cid) => {
    if (!draft) return;
    try {
      const { data } = await api.delete(`/marketing/drafts/${draft.id}/comments/${cid}`);
      setComments(data.comments || []);
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Best-time hint — first channel + first content type
  const bestTimeHint = useMemo(() => {
    const ch = Array.from(channels)[0];
    const t = contentTypes[0];
    if (!ch || !t) return null;
    const rows = bestTimes[ch]?.[t];
    return rows?.length ? rows : null;
  }, [channels, contentTypes, bestTimes]);

  const checklistTotal = Object.keys(checklist).length;
  const checklistDone = Object.values(checklist).filter(Boolean).length;

  // Sync preview tab list to selected channels
  const selectedChannelList = useMemo(() => Array.from(channels), [channels]);
  useEffect(() => {
    if (!selectedChannelList.includes(previewTab) && selectedChannelList[0]) {
      setPreviewTab(selectedChannelList[0]);
    }
  }, [selectedChannelList, previewTab]);

  return (
    <div className="flex flex-col max-h-[90vh]" data-testid="planboard-editor">
      {/* -------- Sticky header with title + actions -------- */}
      <div className="flex items-center gap-2 px-5 pt-5 pb-3 border-b border-slate-100 bg-white sticky top-0 z-10">
        <Input
          className="rounded-sm text-lg font-bold border-none focus-visible:ring-0 px-0 shadow-none flex-1 placeholder:text-slate-400"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Untitled post"
          data-testid="planboard-title"
        />
        {draft && (
          <button onClick={remove} className="text-slate-400 hover:text-red-600 p-1.5" data-testid="planboard-editor-delete" title="Delete">
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {/* -------- Platform Preview (TOP) -------- */}
        <PlatformPreview
          platforms={selectedChannelList}
          activePlatform={previewTab}
          onTabChange={setPreviewTab}
          caption={perPlatformCopy[previewTab] ?? caption}
          onCaptionChange={(v) => {
            // First edit for this platform creates a per-platform override.
            // If perPlatformCopy for this tab already exists (even empty string), keep updating it.
            setPerPlatformCopy({ ...perPlatformCopy, [previewTab]: v });
          }}
          mediaUrls={mediaUrls}
        />

        {/* -------- Status + Owner row -------- */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-overline">Status</Label>
            <div className="flex gap-1 flex-wrap mt-1">
              {STATUSES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => changeStatusInline(s.id)}
                  className={`text-[10px] px-2 py-1 rounded-sm inline-flex items-center gap-1 ${status === s.id ? s.pill : "text-slate-600 hover:bg-slate-100"}`}
                  data-testid={`planboard-status-${s.id}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />{s.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="label-overline">Owner <span className="text-[#F54800]">*</span></Label>
            <Select value={ownerId || ANY} onValueChange={(v) => setOwnerId(v === ANY ? "" : v)}>
              <SelectTrigger className="rounded-sm mt-1 h-8 text-xs" data-testid="planboard-owner"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>— Unassigned —</SelectItem>
                {(members || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* -------- Importance (Phase 101) -------- */}
        <div>
          <Label className="label-overline">Importance <span className="text-[#F54800]">*</span></Label>
          <Select value={importance} onValueChange={setImportance}>
            <SelectTrigger className="rounded-sm mt-1 h-8 text-xs" data-testid="planboard-importance"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Mid</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* -------- Priority link (Phase 84.33) -------- */}
        <div>
          <Label className="label-overline">Priority link <span className="text-slate-400 font-normal">(optional)</span></Label>
          <Select value={priorityId || ANY} onValueChange={(v) => setPriorityId(v === ANY ? "" : v)}>
            <SelectTrigger className="rounded-sm mt-1 h-8 text-xs" data-testid="planboard-priority"><SelectValue placeholder="— No priority link —" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>— No priority link —</SelectItem>
              {openPriorities.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.title}{p.project_name ? ` · ${p.project_name}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-slate-400 mt-1">
            Also mirrors to the linked daily task so publishing the post ticks the priority off.
          </p>
        </div>

        {/* -------- Content types -------- */}
        <ChipGroup label="Content types" testId="planboard-types" values={contentTypes} options={allContentTypes} onToggle={toggleContentType}
          allowAdd inputValue={newTypeInput} onInputChange={setNewTypeInput}
          onAdd={(v) => { setCustomTypes((arr) => arr.includes(v) ? arr : [...arr, v]); setContentTypes((arr) => arr.includes(v) ? arr : [...arr, v]); }} />

        {/* -------- Platforms -------- */}
        <div>
          <Label className="label-overline">Platforms</Label>
          <div className="flex gap-1 flex-wrap mt-1">
            {CHANNELS.map((c) => {
              const active = channels.has(c.id);
              const Icon = c.Icon;
              return (
                <button key={c.id} type="button" onClick={() => toggleChannel(c.id)}
                  className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm border-2 transition-colors ${active ? "border-transparent text-white" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}
                  style={active ? { background: c.color } : {}}
                  data-testid={`planboard-channel-${c.id}`}>
                  <Icon size={11} /> {c.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* -------- Schedule -------- */}
        <div>
          <Label className="label-overline">Schedule ({TIMEZONES.find((t) => t.id === tz)?.label})</Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <Input type="date" className="rounded-sm" value={dateStr} onChange={(e) => setDateStr(e.target.value)} data-testid="planboard-date" />
            <Input type="time" className="rounded-sm" value={timeStr} onChange={(e) => setTimeStr(e.target.value)} data-testid="planboard-time" />
          </div>
          {bestTimeHint && (
            <div className="mt-1 text-[10px] text-slate-500 flex items-center gap-1 flex-wrap" data-testid="planboard-best-time">
              <Sparkles size={9} className="text-[#F54800]" /> Best times for this platform + type:
              {bestTimeHint.map((slot) => (
                <button
                  key={slot}
                  type="button"
                  onClick={() => {
                    const time = slot.split(" ").pop();
                    if (time) setTimeStr(time);
                  }}
                  className="px-1.5 py-0.5 rounded-sm bg-[#F54800]/10 text-[#F54800] hover:bg-[#F54800]/20"
                  data-testid={`planboard-best-slot-${slot.replace(/\s|:/g, "")}`}
                >{slot}</button>
              ))}
            </div>
          )}
        </div>

        {/* -------- Media -------- */}
        <div>
          <Label className="label-overline">Images {mediaUrls.length > 0 && <span className="text-slate-400 font-normal">· {mediaUrls.length}/10</span>}</Label>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <input id="planboard-file" type="file" accept="image/*" multiple onChange={uploadImages} className="hidden" data-testid="planboard-file-input" />
            <label htmlFor="planboard-file" className={`text-[11px] inline-flex items-center gap-1.5 border border-slate-200 hover:border-slate-400 rounded-sm px-2 py-1 cursor-pointer bg-white ${mediaUrls.length >= 10 ? "opacity-40 pointer-events-none" : ""}`} data-testid="planboard-upload">
              <ImageIcon size={12} /> {uploading ? "Uploading…" : mediaUrls.length === 0 ? "Add images" : "Add more"}
            </label>
          </div>
          {mediaUrls.length > 0 && (
            <div className="mt-2 flex gap-1.5 flex-wrap" data-testid="planboard-media-strip">
              {mediaUrls.map((url, idx) => (
                <div key={`${url}-${idx}`} className="relative group">
                  <img src={url} alt="" className="w-14 h-14 object-cover rounded-sm border border-slate-200" />
                  <button type="button" onClick={() => setMediaUrls((arr) => arr.filter((_, i) => i !== idx))} className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white border border-slate-300 text-slate-500 hover:text-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100"><X size={9} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* -------- Caption (shared default) -------- */}
        <div>
          <Label className="label-overline">Caption</Label>
          <Textarea rows={4} className="rounded-sm mt-1 resize-none" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="planboard-caption" placeholder="Default caption used when a platform has no custom copy…" />
        </div>

        {/* -------- Production checklist -------- */}
        <Collapsible label={<span className="inline-flex items-center gap-1"><ClipboardCheck size={12} /> Production checklist <span className="text-slate-500 font-mono">· {checklistDone}/{checklistTotal}</span></span>} testId="planboard-checklist">
          <div className="space-y-1">
            {Object.entries(checklist).map(([k, v]) => (
              <label key={k} className="flex items-center gap-2 text-xs group" data-testid={`planboard-check-${k}`}>
                <input type="checkbox" checked={v} onChange={(e) => setChecklist({ ...checklist, [k]: e.target.checked })} className="w-3.5 h-3.5 accent-[#F54800]" />
                <span className={v ? "line-through text-slate-400 flex-1" : "text-slate-700 flex-1"}>{k}</span>
                <button type="button" onClick={() => { const next = { ...checklist }; delete next[k]; setChecklist(next); }} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-600" title="Remove"><X size={10} /></button>
              </label>
            ))}
            <div className="flex items-center gap-1 mt-2">
              <Input className="rounded-sm h-7 text-xs" placeholder="+ Add item…" value={newChecklistKey} onChange={(e) => setNewChecklistKey(e.target.value)} onKeyDown={(e) => {
                if (e.key === "Enter" && newChecklistKey.trim()) { setChecklist({ ...checklist, [newChecklistKey.trim()]: false }); setNewChecklistKey(""); }
              }} data-testid="planboard-check-add" />
            </div>
          </div>
        </Collapsible>

        {/* -------- Asset links -------- */}
        <Collapsible label={<span className="inline-flex items-center gap-1"><LinkIcon size={12} /> Asset links <span className="text-slate-500 font-mono">· {assetLinks.length}</span></span>} testId="planboard-assets">
          <div className="space-y-1">
            {assetLinks.map((l, i) => (
              <div key={i} className="flex items-center gap-1" data-testid={`planboard-asset-${i}`}>
                <Input className="rounded-sm h-7 text-xs flex-1" placeholder="Label" value={l.label} onChange={(e) => { const n = [...assetLinks]; n[i] = { ...n[i], label: e.target.value }; setAssetLinks(n); }} />
                <Input className="rounded-sm h-7 text-xs flex-1" placeholder="https://drive…" value={l.url} onChange={(e) => { const n = [...assetLinks]; n[i] = { ...n[i], url: e.target.value }; setAssetLinks(n); }} />
                {l.url && <a href={l.url} target="_blank" rel="noreferrer" className="text-[10px] px-1 text-[#F54800] hover:underline">Open</a>}
                <button onClick={() => setAssetLinks(assetLinks.filter((_, x) => x !== i))} className="text-slate-300 hover:text-red-600"><X size={10} /></button>
              </div>
            ))}
            <button type="button" onClick={() => setAssetLinks([...assetLinks, { label: "", url: "" }])} className="text-[11px] text-slate-500 hover:text-[#F54800] inline-flex items-center gap-1" data-testid="planboard-asset-add"><Plus size={11} /> Add link</button>
          </div>
        </Collapsible>

        {/* -------- Final copy per platform -------- */}
        <Collapsible label={<span className="inline-flex items-center gap-1"><PencilRuler size={12} /> Final copy · per platform</span>} testId="planboard-final-copy">
          <div className="flex gap-1 mb-2 flex-wrap">
            {Array.from(channels).map((cid) => {
              const c = CHANNEL_MAP[cid];
              if (!c) return null;
              const active = cid === previewTab;
              const Icon = c.Icon;
              return (
                <button key={cid} onClick={() => setPreviewTab(cid)} className={`text-[11px] px-2 py-1 rounded-sm inline-flex items-center gap-1 ${active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`planboard-copy-tab-${cid}`}>
                  <Icon size={10} /> {c.label}
                </button>
              );
            })}
          </div>
          <Textarea
            rows={5}
            className="rounded-sm resize-none text-xs"
            placeholder={`Final copy for ${CHANNEL_MAP[previewTab]?.label || "this platform"}…`}
            value={perPlatformCopy[previewTab] || ""}
            onChange={(e) => setPerPlatformCopy({ ...perPlatformCopy, [previewTab]: e.target.value })}
            data-testid={`planboard-copy-body-${previewTab}`}
          />
        </Collapsible>

        {/* -------- Dependencies -------- */}
        <Collapsible label={<span className="inline-flex items-center gap-1"><ListTree size={12} /> Dependencies <span className="text-slate-500 font-mono">· {dependencies.length}</span></span>} testId="planboard-deps">
          <div className="space-y-1">
            {dependencies.map((d, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto_auto] gap-1 items-center" data-testid={`planboard-dep-${i}`}>
                <Input className="rounded-sm h-7 text-xs" placeholder="Task" value={d.task || ""} onChange={(e) => { const n = [...dependencies]; n[i] = { ...n[i], task: e.target.value }; setDependencies(n); }} />
                <Select value={d.person_id || ANY} onValueChange={(v) => { const n = [...dependencies]; n[i] = { ...n[i], person_id: v === ANY ? "" : v, person: v === ANY ? "" : (members.find((m) => m.id === v)?.name || "") }; setDependencies(n); }}>
                  <SelectTrigger className="rounded-sm h-7 text-xs"><SelectValue placeholder="Person" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>— Anyone —</SelectItem>
                    {(members || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={d.status || "planned"} onValueChange={(v) => { const n = [...dependencies]; n[i] = { ...n[i], status: v }; setDependencies(n); }}>
                  <SelectTrigger className="rounded-sm h-7 text-xs w-[100px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planned">Planned</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="done">Done</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                  </SelectContent>
                </Select>
                <button onClick={() => setDependencies(dependencies.filter((_, x) => x !== i))} className="text-slate-300 hover:text-red-600"><X size={10} /></button>
              </div>
            ))}
            <button type="button" onClick={() => setDependencies([...dependencies, { task: "", person: "", status: "planned" }])} className="text-[11px] text-slate-500 hover:text-[#F54800] inline-flex items-center gap-1" data-testid="planboard-dep-add"><Plus size={11} /> Add dependency</button>
          </div>
        </Collapsible>

        {/* -------- Comments -------- */}
        {draft && (
          <Collapsible label={<span className="inline-flex items-center gap-1"><MessageSquare size={12} /> Comments <span className="text-slate-500 font-mono">· {comments.length}</span></span>} testId="planboard-comments">
            <div className="space-y-2">
              {comments.map((c) => (
                <div key={c.id} className="text-xs bg-slate-50 rounded-sm p-2" data-testid={`planboard-comment-${c.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-slate-900">{c.user_name}</span>
                    <span className="text-[10px] text-slate-500">{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-slate-800 whitespace-pre-wrap break-words">{c.body}</p>
                  <button onClick={() => deleteComment(c.id)} className="text-[9px] text-slate-400 hover:text-red-600 mt-1">Delete</button>
                </div>
              ))}
              <div className="flex items-start gap-1">
                <Textarea rows={2} className="rounded-sm text-xs flex-1 resize-none" placeholder="Leave a comment…" value={newCommentBody} onChange={(e) => setNewCommentBody(e.target.value)} data-testid="planboard-comment-input" />
                <Button size="sm" className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" onClick={addComment} disabled={!newCommentBody.trim()} data-testid="planboard-comment-send">
                  <Send size={11} />
                </Button>
              </div>
            </div>
          </Collapsible>
        )}
      </div>

      {/* -------- Sticky footer with save buttons -------- */}
      <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-100 bg-white sticky bottom-0">
        <Button onClick={onClose} variant="ghost" className="rounded-sm text-slate-600" data-testid="planboard-cancel">Cancel</Button>
        <div className="flex-1" />
        <Button onClick={() => save({ saveAsDraft: true })} disabled={saving} variant="outline" className="rounded-sm" data-testid="planboard-save-draft">Save as draft</Button>
        <Button onClick={() => save()} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="planboard-save">
          {saving ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Send size={13} className="mr-1.5" />}
          {dateStr ? "Schedule" : "Save"}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/*  Platform Preview — top of modal, tabbed by platform                       */
/* ------------------------------------------------------------------------- */
function PlatformPreview({ platforms, activePlatform, onTabChange, caption, onCaptionChange, mediaUrls }) {
  if (!platforms || platforms.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 py-8 text-center text-xs text-slate-500" data-testid="planboard-preview-empty">
        Select at least one platform below to see a live preview.
      </div>
    );
  }
  const active = CHANNEL_MAP[activePlatform] || CHANNEL_MAP[platforms[0]];
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/70 overflow-hidden" data-testid="planboard-preview">
      {/* Tabs */}
      <div className="flex items-center gap-1 px-2 pt-2 flex-wrap">
        {platforms.map((cid) => {
          const c = CHANNEL_MAP[cid];
          if (!c) return null;
          const isActive = cid === activePlatform;
          const Icon = c.Icon;
          return (
            <button
              key={cid}
              onClick={() => onTabChange(cid)}
              className={`text-[11px] px-2.5 py-1.5 rounded-t-sm inline-flex items-center gap-1.5 border-b-2 transition-colors ${isActive ? "bg-white border-transparent text-slate-900 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}
              style={isActive ? { borderBottomColor: c.color } : {}}
              data-testid={`planboard-preview-tab-${cid}`}
            >
              <Icon size={12} style={{ color: c.color }} /> {c.label}
            </button>
          );
        })}
      </div>
      {/* Preview surface */}
      <div className="bg-white p-4">
        <PlatformMockup platform={active} caption={caption} onCaptionChange={onCaptionChange} mediaUrls={mediaUrls} />
      </div>
    </div>
  );
}

function PlatformMockup({ platform, caption, onCaptionChange, mediaUrls }) {
  const heroImg = mediaUrls?.[0];
  const brand = platform?.color || "#111827";
  const Icon = platform?.Icon;
  return (
    <div className="max-w-md mx-auto rounded-md border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid={`planboard-mockup-${platform.id}`}>
      {/* Header (avatar + name) */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: brand }}>
          {Icon ? <Icon size={14} /> : null}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-slate-900 truncate">your.brand</div>
          <div className="text-[10px] text-slate-500">Sponsored · Just now</div>
        </div>
        <MoreHorizontal size={14} className="text-slate-400" />
      </div>

      {/* Media */}
      {heroImg ? (
        <div className="aspect-square bg-slate-100 relative">
          <img src={heroImg} alt="" className="w-full h-full object-cover" />
          {mediaUrls.length > 1 && (
            <div className="absolute top-2 right-2 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded-full">
              1 / {mediaUrls.length}
            </div>
          )}
        </div>
      ) : (
        <div className="aspect-square bg-slate-50 flex items-center justify-center text-slate-300">
          <ImageIcon size={40} />
        </div>
      )}

      {/* Action row (matches platform vibe) */}
      <div className="flex items-center gap-3 px-3 pt-3 pb-1 text-slate-700">
        {platform.id === "linkedin" ? (
          <>
            <ThumbsUp size={16} /><MessageCircle size={16} /><Repeat2 size={16} /><Send size={16} />
          </>
        ) : platform.id === "x" || platform.id === "threads" ? (
          <>
            <MessageCircle size={16} /><Repeat2 size={16} /><Heart size={16} /><Share2 size={16} />
          </>
        ) : platform.id === "youtube" ? (
          <>
            <ThumbsUp size={16} /><MessageCircle size={16} /><Share2 size={16} />
          </>
        ) : (
          <>
            <Heart size={16} /><MessageCircle size={16} /><Send size={16} />
            <Bookmark size={16} className="ml-auto" />
          </>
        )}
      </div>

      {/* Caption — editable */}
      <div className="px-3 pb-3 pt-1">
        <div className="text-xs">
          <span className="font-semibold text-slate-900 mr-1">your.brand</span>
        </div>
        <Textarea
          rows={3}
          className="mt-1 rounded-sm resize-none text-xs border-slate-100 focus-visible:border-slate-300 bg-slate-50/60"
          value={caption}
          onChange={(e) => onCaptionChange(e.target.value)}
          placeholder={`Write your ${platform.label} caption…`}
          data-testid={`planboard-preview-caption-${platform.id}`}
        />
        <div className="text-[10px] text-slate-400 mt-1">
          {caption?.length || 0} chars · editing directly updates this platform&apos;s copy
        </div>
      </div>
    </div>
  );
}

/** Reusable chip-group with add-your-own. */
function ChipGroup({ label, testId, values, options, onToggle, allowAdd, inputValue, onInputChange, onAdd }) {
  return (
    <div>
      <Label className="label-overline">{label}</Label>
      <div className="mt-1 flex flex-wrap gap-1" data-testid={testId}>
        {options.map((opt) => {
          const active = values.includes(opt);
          return (
            <button key={opt} type="button" onClick={() => onToggle(opt)}
              className={`text-[11px] px-2 py-0.5 rounded-sm border ${active ? "bg-[#F54800] text-white border-[#F54800]" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
              data-testid={`${testId}-opt-${opt.replace(/\s|\//g, "_")}`}>
              {opt}
            </button>
          );
        })}
        {allowAdd && (
          <Input className="rounded-sm h-6 text-[11px] w-32" placeholder="+ add…" value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && inputValue.trim()) { onAdd(inputValue.trim()); onInputChange(""); }
            }}
            data-testid={`${testId}-add`} />
        )}
      </div>
    </div>
  );
}

/** Compact collapsible for the detail-panel sections. */
function Collapsible({ label, children, testId, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-slate-100 pt-3" data-testid={testId}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between text-xs font-semibold text-slate-700 hover:text-slate-900" data-testid={`${testId}-toggle`}>
        <span>{label}</span>
        <ChevronRight size={12} className={`transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}
